import {
  AutoCompleteOption,
  ClientTeamMemberBase as SharedClientTeamMemberBase,
} from '@ths/shared';
import { useMemo, useState } from 'react';

import {
  ClientTeamMember,
  useRosterFindGuestByEdipiQuery,
} from '../generated/graphql';
import { useAppSelector } from '../store/store';
import { selectUnknownGuest } from '../store/unknownGuestSlice';
import { popToast } from '../utilities/popToast';

export type ClientTeamMemberOption = Pick<
  ClientTeamMember,
  | 'id'
  | 'firstName'
  | 'lastName'
  | 'edipi'
  | 'rank'
  | 'gender'
  | 'semiUniqueIdentifier'
  | 'employeeNumber'
  | 'mission'
  | 'jobTitle'
  | 'taskForce'
  | 'unknownGuest'
>;

export interface UseClientClientTeamMemberSelectorOptions {
  initialQuery?: string;
  onQueryChange?: (query: string) => void;
}

export interface UseClientClientTeamMemberSelectorReturn {
  /** Guests wrapped with metadata for the AutoComplete component */
  wrappedGuests: Array<AutoCompleteOption<SharedClientTeamMemberBase>>;

  /** Current search query */
  query: string;

  /** Handler for query changes */
  onQueryChange: (newQuery: string) => void;

  /** Loading state from GraphQL query */
  loading: boolean;

  /** Error state from GraphQL query */
  hasError: boolean;

  /** Unknown guest from Redux store (if exists) */
  unknownGuest: ClientTeamMemberOption | null;
}

/**
 * Hook for managing client team member selection in client portal
 *
 * Handles:
 * - GraphQL queries for guest data
 * - Data transformation with metadata
 * - Unknown guest from Redux store
 * - Query state management
 *
 * @example
 * ```tsx
 * const {
 *   wrappedGuests,
 *   query,
 *   onQueryChange,
 *   unknownGuest,
 *   loading,
 * } = useClientClientTeamMemberSelector({
 *   initialQuery: '',
 * });
 * ```
 */
export const useClientClientTeamMemberSelector = ({
  initialQuery,
  onQueryChange: externalOnQueryChange,
}: UseClientClientTeamMemberSelectorOptions): UseClientClientTeamMemberSelectorReturn => {
  const [query, setQuery] = useState(initialQuery ?? '');

  // Fetch unknown guest from Redux store
  const unknownGuest = useAppSelector(selectUnknownGuest);

  // Fetch guests via GraphQL
  const { data: clientTeamMembersData, loading } =
    useRosterFindGuestByEdipiQuery({
      variables: {
        searchText: query || undefined,
        limit: 100,
      },
      onError: (error) => {
        popToast(error.message, { type: 'error' });
      },
    });

  // Extract and transform guest data
  const guests: Array<SharedClientTeamMemberBase> = useMemo(
    () =>
      clientTeamMembersData?.clientTeamMembers?.map(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        ({ __typename, ...rest }) => rest as SharedClientTeamMemberBase,
      ) ?? [],
    [clientTeamMembersData],
  );

  // Wrap guests with metadata for AutoComplete component
  const wrappedGuests: Array<AutoCompleteOption<SharedClientTeamMemberBase>> =
    useMemo(
      () =>
        guests.map((guest) => ({
          value: guest,
          metadata: {
            // Client portal doesn't currently use disabled/label metadata
            // but the structure is here for future use
          },
        })),
      [guests],
    );

  // Handle query changes
  const handleQueryChange = (newQuery: string) => {
    setQuery(newQuery);
    externalOnQueryChange?.(newQuery);
  };

  return {
    wrappedGuests,
    query,
    onQueryChange: handleQueryChange,
    loading,
    hasError: false, // Could add error state if needed
    unknownGuest: unknownGuest as ClientTeamMemberOption | null,
  };
};
